import { DateTime } from "luxon";
import z, { ZodEffects, ZodSchema } from "zod";

const DonorSummaryColumns = z.enum([
  "totalDonationInCents",
  "totalDonationCount",
  "lastDonationDate",
  "lastDonationInCents",
  "highestDonationInCents",
  "averageDonationInCents",
  "firstDonationDate",
  "totalLastYearInCents",
  "totalThisYearInCents",
  "totalLastMonthInCents",
  "totalThisMonthInCents",
  "recurringDonor",
  "recurringDonation",
  "hasDonated",
  "hasMobileDonation",
]);

const DonorSummaryFieldSourceSchema = z.object({
  source: z.literal("donorSummaryField"),
  id: DonorSummaryColumns,
});

const AudienceContactFieldSourceSchema = z.object({
  source: z.literal("audienceContactField"),
  id: z.string().uuid(),
});

const AllFieldSourceSchema = z.discriminatedUnion("source", [
  DonorSummaryFieldSourceSchema,
  AudienceContactFieldSourceSchema,
]);

const OneOfOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  values: z.array(z.string()).or(z.array(z.number())).or(z.array(z.boolean())),
});

const MatchesOperatorSchema = z.object({
  field: AudienceContactFieldSourceSchema,
  value: z.string(),
});

const EqualsOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  value: z.string().or(z.number()).or(z.boolean()),
});

const GteOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  value: z.number().or(z.string()),
});

const GtOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  value: z.number().or(z.string()),
});

const LteOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  value: z.number().or(z.string()),
});

const LtOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  value: z.number().or(z.string()),
});

const RangeOperatorSchema = z.object({
  field: AllFieldSourceSchema,
  values: z.union([z.string().array().length(2), z.number().array().length(2)]),
});

const ContainsOperatorSchema = z.object({
  field: AudienceContactFieldSourceSchema,
  value: z.string(),
});

const DateFilterKeys = z.enum([
  "ALL_TIME",
  "LAST_MONTH",
  "THIS_MONTH",
  "LAST_YEAR",
  "THIS_YEAR",
  "CUSTOM_DATE_RANGE",
]);

export const DateTimeSchema = z.preprocess(
  value => {
    if (typeof value === "string" || value instanceof Date) {
      return DateTime.fromJSDate(new Date(value));
    }
    return value as DateTime;
  },
  z.custom<DateTime>(value => value instanceof DateTime)
) as ZodEffects<
  z.ZodType<DateTime<boolean>, z.ZodTypeDef, DateTime<boolean>>,
  DateTime<boolean>,
  DateTime<boolean>
>;

const DateFilterFormSchema = z.object({
  dateFilter: z
    .object({
      key: DateFilterKeys,
      startDate: z.string().or(DateTimeSchema).nullish(),
      endDate: z.string().or(DateTimeSchema).nullish(),
    })
    .nullish(),
});

export const AudienceQueryOperatorSchema: ZodSchema = z.discriminatedUnion(
  "operator",
  [
    z.object({
      operator: z.enum(["and", "or"]),
      clauses: z.array(z.lazy(() => AudienceQueryOperatorSchema)),
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("not"),
      clause: z.lazy(() => AudienceQueryOperatorSchema),
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("oneOf"),
      ...OneOfOperatorSchema.shape,
    }),
    z.object({
      operator: z.literal("matches"),
      ...MatchesOperatorSchema.shape,
    }),
    z.object({
      operator: z.literal("equals"),
      ...EqualsOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("gte"),
      ...GteOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("gt"),
      ...GtOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("lte"),
      ...LteOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("lt"),
      ...LtOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("between"),
      ...RangeOperatorSchema.shape,
      ...DateFilterFormSchema.shape,
    }),
    z.object({
      operator: z.literal("contains"),
      ...ContainsOperatorSchema.shape,
    }),
    z.object({
      operator: z.literal("isNull"),
      field: AllFieldSourceSchema,
    }),
    z.object({
      operator: z.literal("notNull"),
      field: AllFieldSourceSchema,
    }),
  ]
);

export type AudienceQueryOperator = z.infer<typeof AudienceQueryOperatorSchema>;

export enum AudienceTargetingFilterCategory {
  Location = "location",
  DonationHistory = "donationHistory",
  CustomField = "customField",
}

const AudienceTargetingFilterCategorySchema = z.nativeEnum(
  AudienceTargetingFilterCategory
);

export const AudienceTargetingFilterDonationHistoryOperations = [
  "totalDonationGt",
  "totalDonationLt",
  "totalDonationBetween",
  "isRecurringDonor",
  "notRecurringDonor",
  "recurringDonationAmountGt",
  "recurringDonationAmountLt",
  "recurringDonationAmountBetween",
  "hasDonated",
  "notHasDonated",
  "lastDonationGt",
  "lastDonationLt",
  "lastDonationBetween",
  "numberOfDonationsGt",
  "numberOfDonationsLt",
  "numberOfDonationsBetween",
  "averageDonationGt",
  "averageDonationLt",
  "averageDonationBetween",
  "highestDonationGt",
  "highestDonationLt",
  "highestDonationBetween",
  "hasMobileDonation",
  "notHasMobileDonation",
] as const;

const AudienceTargetingFilterDonationHistoryOperation = z.enum(
  AudienceTargetingFilterDonationHistoryOperations
);

const AudienceTargetingFilterOperationSchema = z
  .enum([
    "isInState",
    "isInCustomState",
    "notInState",
    "notInCustomState",
    "stateIsNull",
    "isInZip",
    "isInCustomZip",
    "notInZip",
    "notInCustomZip",
    "isInZipRadius",
    "isInCustomZipRadius",
    "notInZipRadius",
    "notInCustomZipRadius",
    "zipIsNull",
    "contains",
    "notContains",
    "matches",
    "equals",
    "notEquals",
    "boolean",
    "customFalse",
    "customTrue",
    "customIsNull",
    "between",
    "notBetween",
    "oneOf",
    "notOneOf",
    "gt",
    "lt",
    "isNull",
  ])
  .or(AudienceTargetingFilterDonationHistoryOperation);

export type AudienceTargetingFilterOperation = z.infer<
  typeof AudienceTargetingFilterOperationSchema
>;

const AudienceTargetingFieldTypeSchema = z.enum([
  "STATE",
  "ZIP",
  "ZIP_RADIUS",
  "NUMBER",
  "NUMBER_RANGE",
  "BOOLEAN_TRUE",
  "BOOLEAN_FALSE",
  "TEXT",
  "ENUM",
  "BOOLEAN",
  "CURRENCY",
  "CURRENCY_RANGE",
  "IS_NULL",
]);

export type AudienceTargetingFieldType = z.infer<
  typeof AudienceTargetingFieldTypeSchema
>;

const FilterConfigSchema = z.object({
  centerZips: z.array(z.string()),
  radius: z.string(),
});

function checkRangeValue(
  data: { fieldType: string; values?: unknown[] | null },
  index: number
) {
  const isRangeType =
    data.fieldType === "NUMBER_RANGE" || data.fieldType === "CURRENCY_RANGE";
  if (isRangeType) {
    if (data.values === null) {
      return false;
    }
    return (
      data.values?.[index] !== undefined &&
      !Number.isNaN(Number(data.values?.[index]))
    );
  }
  return true;
}

function rangeValueError(index: number) {
  return {
    message: "Value is required and must be a number",
    path: ["values", index],
  };
}

const TargetingFormFilterSchema = z
  .object({
    filterCategory: AudienceTargetingFilterCategorySchema,
    filterOperation: AudienceTargetingFilterOperationSchema,
    fieldType: AudienceTargetingFieldTypeSchema,
    values: z.array(z.string()).or(z.array(z.number())).nullish(),
    value: z.union([z.string(), z.number(), z.boolean()]).nullish(),
    fieldName: z.string().nullish(),
    filterConfig: FilterConfigSchema.nullish(),
  })
  .refine(
    data => checkRangeValue(data, 0),
    () => rangeValueError(0)
  )
  .refine(
    data => checkRangeValue(data, 1),
    () => rangeValueError(1)
  )
  .refine(
    data => {
      if (data.filterOperation.toLowerCase().includes("isnull")) {
        return true;
      }
      const hasValue = data.value !== undefined && data.value !== "";
      const hasValues =
        data.values !== undefined &&
        data.values?.length &&
        data.values.length > 0;
      return hasValue || hasValues;
    },
    data => {
      switch (data.fieldType) {
        case "STATE": {
          return {
            message: "At least one state must be selected",
            path: ["values"],
          };
        }
        case "ZIP":
        case "ZIP_RADIUS": {
          return {
            message: "At least one zip code must be provided",
            path: ["values"],
          };
        }
        case "NUMBER": {
          return {
            message: "A number is required",
            path: ["value"],
          };
        }
        case "BOOLEAN": {
          return {
            message: "A value is required",
            path: ["value"],
          };
        }
        case "ENUM": {
          return {
            message: "At least one option must be selected",
            path: ["values"],
          };
        }
        case "NUMBER_RANGE":
        case "CURRENCY_RANGE": {
          return {
            message: "A number range is required",
            path: ["values"],
          };
        }
        default: {
          return {
            message: "A value is required",
            path: ["value"],
          };
        }
      }
    }
  );

export const TargetingFormSchema = z.object({
  targetGroups: z.array(
    z.object({
      clauses: z.array(TargetingFormFilterSchema),
      ...DateFilterFormSchema.shape,
    })
  ),
});

export const SavedSearchSchema = TargetingFormSchema.extend({
  name: z.string(),
});
export type SavedSearchTargetingForm = z.infer<typeof SavedSearchSchema>;

export type TargetingForm = z.infer<typeof TargetingFormSchema>;
export type TargetingFormFilter = z.infer<typeof TargetingFormFilterSchema>;

function determineOperator(filterOperation: AudienceTargetingFilterOperation) {
  switch (filterOperation) {
    case "isInState":
    case "isInCustomState":
    case "notInState":
    case "notInCustomState":
    case "isInZip":
    case "isInCustomZip":
    case "notInZip":
    case "notInCustomZip":
    case "isInZipRadius":
    case "isInCustomZipRadius":
    case "notInZipRadius":
    case "notInCustomZipRadius":
    case "oneOf":
    case "notOneOf": {
      return "oneOf";
    }
    case "totalDonationGt":
    case "recurringDonationAmountGt":
    case "lastDonationGt":
    case "numberOfDonationsGt":
    case "averageDonationGt":
    case "highestDonationGt":
    case "gt": {
      return "gt";
    }
    case "totalDonationLt":
    case "recurringDonationAmountLt":
    case "lastDonationLt":
    case "numberOfDonationsLt":
    case "averageDonationLt":
    case "highestDonationLt":
    case "lt": {
      return "lt";
    }
    case "totalDonationBetween":
    case "recurringDonationAmountBetween":
    case "lastDonationBetween":
    case "numberOfDonationsBetween":
    case "averageDonationBetween":
    case "highestDonationBetween":
    case "between":
    case "notBetween": {
      return "between";
    }
    case "hasDonated": {
      return "notNull";
    }
    case "notHasDonated":
    case "customIsNull":
    case "stateIsNull":
    case "zipIsNull": {
      return "isNull";
    }
    case "isRecurringDonor":
    case "notRecurringDonor":
    case "hasMobileDonation":
    case "notHasMobileDonation":
    case "equals":
    case "notEquals":
    case "boolean":
    case "customFalse":
    case "customTrue": {
      return "equals";
    }
    case "matches": {
      return "matches";
    }
    case "contains":
    case "notContains": {
      return "contains";
    }
    default: {
      throw new Error(`Unknown filterOperation: ${filterOperation}`);
    }
  }
}

function determineField(
  filterOperation: string,
  audienceFields: AudienceFieldNameToId,
  audienceFieldName: string | undefined | null
) {
  switch (filterOperation) {
    case "totalDonationGt":
    case "totalDonationLt":
    case "totalDonationBetween":
      return {
        id: "totalDonationInCents",
        source: "donorSummaryField",
      };
    case "isRecurringDonor":
    case "notRecurringDonor":
      return {
        id: "recurringDonor",
        source: "donorSummaryField",
      };
    case "hasDonated":
    case "notHasDonated":
      return {
        id: "donorPhoneNumber",
        source: "donorSummaryField",
      };
    case "hasMobileDonation":
    case "notHasMobileDonation": {
      return {
        id: "hasMobileDonation",
        source: "donorSummaryField",
      };
    }
    case "lastDonationGt":
    case "lastDonationLt":
    case "lastDonationBetween":
      return {
        id: "lastDonationInCents",
        source: "donorSummaryField",
      };
    case "numberOfDonationsGt":
    case "numberOfDonationsLt":
    case "numberOfDonationsBetween":
      return {
        id: "totalDonationCount",
        source: "donorSummaryField",
      };
    case "averageDonationGt":
    case "averageDonationLt":
    case "averageDonationBetween":
      return {
        id: "averageDonationInCents",
        source: "donorSummaryField",
      };
    case "highestDonationGt":
    case "highestDonationLt":
    case "highestDonationBetween":
      return {
        id: "highestDonationInCents",
        source: "donorSummaryField",
      };
    case "recurringDonationAmountGt":
    case "recurringDonationAmountLt":
    case "recurringDonationAmountBetween": {
      return {
        id: "recurringDonation",
        source: "donorSummaryField",
      };
    }
    case "isInState":
    case "notInState":
    case "stateIsNull": {
      return {
        source: "audienceContactField",
        id: audienceFields["state"],
      };
    }
    case "isInZip":
    case "notInZip":
    case "isInZipRadius":
    case "notInZipRadius":
    case "zipIsNull": {
      return {
        source: "audienceContactField",
        id: audienceFields["zip"],
      };
    }
    case "contains":
    case "notContains":
    case "equals":
    case "notEquals":
    case "matches":
    case "gt":
    case "lt":
    case "between":
    case "notBetween":
    case "customTrue":
    case "customFalse":
    case "oneOf":
    case "notOneOf":
    case "isInCustomState":
    case "notInCustomState":
    case "isInCustomZip":
    case "notInCustomZip":
    case "isInCustomZipRadius":
    case "notInCustomZipRadius":
    case "customIsNull": {
      return {
        source: "audienceContactField",
        id: audienceFields[audienceFieldName!],
      };
    }
  }

  throw new Error(`Unknown filter operation: ${filterOperation}`);
}

type AudienceFieldNameToId = { [fieldName: string]: string };

function buildFilterClause(
  clause: TargetingFormFilter,
  audienceFields: AudienceFieldNameToId
) {
  // Any filter operation in this list will get wrapped in a "not" operator
  const negationOperators = [
    "notInState",
    "notInCustomState",
    "notInZip",
    "notInCustomZip",
    "notInZipRadius",
    "notInCustomZipRadius",
    "notBetween",
    "notEquals",
    "notContains",
    "notOneOf",
  ];

  const operatorObj = {
    operator: determineOperator(clause.filterOperation),
    field: determineField(
      clause.filterOperation,
      audienceFields,
      clause.fieldName
    ),
    ...(clause.value !== undefined ? { value: clause.value } : {}),
    ...(clause.values ? { values: clause.values } : {}),
  };

  if (negationOperators.includes(clause.filterOperation)) {
    return {
      operator: "not",
      clause: operatorObj,
    };
  }

  return operatorObj;
}

export function convertAudienceTargetingFormDataToFilterObj(
  formData: TargetingForm,
  audienceFields: AudienceFieldNameToId
): AudienceQueryOperator {
  return {
    operator: "or",
    clauses: formData.targetGroups.map(tg => {
      return {
        operator: "and",
        clauses: tg.clauses.map(clause =>
          buildFilterClause(clause, audienceFields)
        ),
        dateFilter: tg.dateFilter,
      };
    }),
  };
}
